import React from "react";
import { useFormik } from "formik";
import { Button, Grid } from "@mui/material";

import "components/pages/notificationPreferences/list/NotificationPreferenceList.css";

import Loading from "components/common/loading/Loading";
import SubmitButton from "components/Button/SubmitButton";
import EmptyTableData from "components/common/empty_data/EmptyTableData";
import EmptyContainer from "components/common/empty_container/EmptyContainer";
import AccountFilter from "components/common/filters/account/AccountFilter";

import DiscardPreferenceModal from "../modal/DiscardPreferenceModal";
import NotificationPreferenceTable from "./table/NotificationPreferenceTable";
import useNotificationPreferenceList from "./useNotificationPreferenceList";

import { showErrorToast } from "utils/toast";
import { AlertConfigurationSchema } from "schemas/alertConfiguration";

const NotificationPreferenceList = () => {
  const {
    isLoading,
    newAccount,
    preferences,
    selectedAccount,
    isEditPreference,
    isCancelPreference,
    hasNotificationPreference,

    cancelEdit,
    toggleAccount,
    editPreferences,
    savePreferences,
    cancelAccountToggle,
    discardAccountToggle,
    setIsCancelPreference,
    changeNotificationPreference,
  } = useNotificationPreferenceList();

  const formik = useFormik({
    initialValues: { configurations: preferences },
    initialErrors: {},
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: AlertConfigurationSchema,
    onSubmit: (values) => savePreferences(values.configurations),
  });

  const { resetForm, submitForm, validateForm, setTouched } = formik;

  const mapErrorsToTouched = (errors) => {
    if (!errors || typeof errors !== "object") {
      return true;
    }

    if (Array.isArray(errors)) {
      return errors.map((item) => mapErrorsToTouched(item));
    }

    return Object.keys(errors).reduce((acc, key) => {
      acc[key] = mapErrorsToTouched(errors[key]);
      return acc;
    }, {});
  };

  const focusFirstErrorField = () => {
    const firstErrorNode = document.querySelector(
      ".MuiFormHelperText-root.Mui-error, .MuiInputBase-root.Mui-error, .MuiFormControl-root .Mui-error"
    );

    if (!firstErrorNode) {
      return;
    }

    const formControl = firstErrorNode.closest(".MuiFormControl-root");
    const scrollTarget = formControl || firstErrorNode;

    scrollTarget.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const discardAccountSelection = () => {
    discardAccountToggle();

    if (newAccount === selectedAccount) return;

    resetForm({});
  };

  const handleDiscardEdit = () => {
    cancelEdit();
    resetForm({});
  };

  const handleSave = async () => {
    const validationErrors = await validateForm();

    if (Object.keys(validationErrors || {}).length > 0) {
      setTouched(mapErrorsToTouched(validationErrors), true);
      showErrorToast("Please fix the errors before saving.");

      setTimeout(() => {
        focusFirstErrorField();
      }, 0);

      return;
    }

    submitForm();
  };

  return (
    <div className="preferences">
      <div className="preferences-container">
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          className="preferences-header"
        >
          <Grid item sm={3}>
            <AccountFilter
              displayAll={false}
              value={selectedAccount}
              onChange={changeNotificationPreference}
            />
          </Grid>
          {hasNotificationPreference && (
            <Grid item sm={6} className="d-flex" justifyContent="flex-end">
              {isEditPreference ? (
                <div className="button-group">
                  <Button
                    variant="outlined"
                    color="secondary"
                    className="preferences-edit-button"
                    onClick={() => setIsCancelPreference(true)}
                  >
                    Cancel
                  </Button>
                  <SubmitButton
                    buttonText="Save Preferences"
                    className="preferences-edit-button"
                    onClick={handleSave}
                    disabled={isLoading}
                  />
                </div>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  className="preferences-edit-button"
                  onClick={editPreferences}
                  disabled={!selectedAccount}
                >
                  Edit
                </Button>
              )}
            </Grid>
          )}
        </Grid>
        {isLoading ? (
          <Loading />
        ) : (
          <Grid container>
            <Grid item xs={12} style={{ textAlign: "center" }}>
              {!selectedAccount ? (
                <EmptyContainer
                  noDataText={
                    <div style={{ textAlign: "center" }}>
                      <h3 className="empty__title">
                        Select an account to proceed
                      </h3>
                      <p className="empty__content">
                        Please select an account from the dropdown to configure
                        alert notfication
                      </p>
                    </div>
                  }
                />
              ) : hasNotificationPreference ? (
                <NotificationPreferenceTable
                  formikProps={formik}
                  isEditPreference={isEditPreference}
                />
              ) : (
                <EmptyTableData />
              )}
            </Grid>
          </Grid>
        )}
      </div>

      {toggleAccount && (
        <DiscardPreferenceModal
          confirmCancel={discardAccountSelection}
          onCancel={cancelAccountToggle}
          showDiscardModal={toggleAccount}
        />
      )}

      {isCancelPreference && (
        <DiscardPreferenceModal
          confirmCancel={handleDiscardEdit}
          onCancel={() => setIsCancelPreference(false)}
          showDiscardModal={isCancelPreference}
        />
      )}
    </div>
  );
};

export default NotificationPreferenceList;
